import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Crown, Check, Sparkles, Loader2 } from "lucide-react";

const UpgradeContext = createContext(null);
export const useUpgrade = () => useContext(UpgradeContext);

const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
const PADDLE_PRICE_ID = process.env.REACT_APP_PADDLE_PRICE_ID;

const FEATURES = [
  "Unlimited generations across all 3 tools",
  "Unlock Steam Store Page & Streamer/PR Pitcher",
  "Full generation history storage",
  "Priority support",
];

export const UpgradeProvider = ({ children }) => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);

  const userRef = useRef(user);
  userRef.current = user;
  const paddleInit = useRef(false);

  // Initialize Paddle.js v2 once, with a completion callback that updates user state.
  useEffect(() => {
    let tries = 0;
    const tryInit = () => {
      if (paddleInit.current) return;
      if (window.Paddle && PADDLE_TOKEN) {
        try {
          window.Paddle.Initialize({
            token: PADDLE_TOKEN,
            eventCallback: async (event) => {
              if (event?.name === "checkout.completed") {
                const txn = event?.data?.transaction_id;
                try {
                  const res = await api.post("/billing/paddle-complete", { transaction_id: txn });
                  await checkAuth();
                  if (res.data?.is_pro || res.data?.status === "active") {
                    toast.success("Payment complete — welcome to Pro! 🎉");
                  } else {
                    toast.message("Payment received — your Pro access will activate in a moment.");
                  }
                  setOpen(false);
                } catch {
                  toast.message("Payment received — confirming your subscription shortly…");
                }
              } else if (event?.name === "checkout.payment.failed" || event?.name === "checkout.error") {
                toast.error("Payment was not completed.");
              }
            },
          });
          paddleInit.current = true;
        } catch (e) {
          /* ignore */
        }
      } else if (tries < 15) {
        tries += 1;
        setTimeout(tryInit, 300);
      }
    };
    tryInit();
  }, [checkAuth]);

  const openUpgrade = (r = "") => {
    setReason(r);
    setOpen(true);
  };

  const startCheckout = () => {
    const u = userRef.current;
    if (!window.Paddle || !PADDLE_TOKEN || !PADDLE_PRICE_ID) {
      toast.error("Checkout is unavailable right now. Please try again shortly.");
      return;
    }
    if (!u?.email) {
      toast.error("Please sign in before subscribing.");
      return;
    }
    window.Paddle.Checkout.open({
      items: [{ priceId: PADDLE_PRICE_ID, quantity: 1 }],
      customer: { email: u.email },
      customData: { user_id: String(u.user_id) },
      settings: {
        displayMode: "overlay",
        theme: "dark",
        successUrl: `${window.location.origin}/pricing?checkout=success`,
      },
    });
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!promo.trim()) return;
    setBusy(true);
    try {
      await api.post("/billing/promo", { code: promo.trim() });
      await checkAuth();
      toast.success("Promo applied — you're a Pro Member! 🎉");
      setPromo("");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid promo code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <UpgradeContext.Provider value={{ openUpgrade, startCheckout }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-zinc-900 border border-teal-500/30 rounded-2xl max-w-md p-0 overflow-hidden"
          data-testid="upgrade-modal"
        >
          <div className="relative p-6">
            <DialogTitle className="sr-only">Upgrade to IndieLaunchkit Pro</DialogTitle>
            <DialogDescription className="sr-only">
              Unlock unlimited generations and full access to all three tools.
            </DialogDescription>
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-10 w-10 rounded-xl bg-teal-400 flex items-center justify-center">
                  <Crown className="h-5 w-5 text-zinc-950" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300">IndieLaunchkit Pro</p>
                  <h2 className="font-heading text-xl font-black tracking-tight text-zinc-100">Upgrade to unlock</h2>
                </div>
              </div>

              {reason && <p className="text-sm text-zinc-400 mb-4">{reason}</p>}

              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-4xl font-black tracking-tighter text-zinc-100">$15</span>
                <span className="text-zinc-500 font-mono text-sm">/ month</span>
                <span className="text-zinc-600 font-mono text-xs">or $0 with promo</span>
              </div>

              <ul className="space-y-2.5 mb-6">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-teal-400/15 flex items-center justify-center">
                      <Check className="h-3.5 w-3.5 text-teal-300" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={startCheckout}
                data-testid="modal-upgrade-btn"
                className="w-full flex items-center justify-center gap-2 bg-teal-400 text-zinc-950 font-semibold py-3 rounded-xl hover:bg-teal-300 transition-colors duration-200 active:scale-[0.99]"
              >
                <Sparkles className="h-4 w-4" /> Upgrade to Pro — $15/mo
              </button>

              <form onSubmit={redeem} className="mt-3 flex gap-2" data-testid="modal-promo-form">
                <Input
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  data-testid="modal-promo-input"
                  placeholder="Have a promo code?"
                  className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="modal-apply-promo-btn"
                  className="shrink-0 border border-zinc-700 text-teal-300 font-medium px-5 rounded-xl hover:border-teal-500/40 hover:bg-teal-400/10 transition-colors duration-200"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                </button>
              </form>

              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/pricing");
                }}
                className="mt-4 w-full text-center text-xs text-zinc-500 hover:text-teal-300 font-mono transition-colors duration-200"
                data-testid="modal-see-pricing-link"
              >
                See full pricing →
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </UpgradeContext.Provider>
  );
};
